import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ErrorBoundary, ViewExtraLarge, ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { SearchResultsStore } from 'stores/search-results-store';

import { SearchFormFields } from 'src/common/models';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';

import { FiltersToggle } from 'components/search/sort-filter/filters-toggle';
import { MapPreview } from 'src/common/components/search/map/map-preview';
import { PlaybackFilter } from 'components/search/playback/playback-filter';
import { ResultsSubHeader } from './results-sub-header';

export interface ResultsHeaderProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}

export interface ResultsHeaderState {
  resultsHeaderActive: boolean;
}

@withLocalization
@inject('searchResults')
@observer
export class ResultsHeader extends React.Component<ResultsHeaderProps, ResultsHeaderState> {
  private searchResultsHeaderWrapper;

  public static defaultProps = {
    l10n: null,
    withMapButton: false,
    withFilterButton: false,
  };

  constructor(props: ResultsHeaderProps) {
    super(props);

    this.state = { resultsHeaderActive: false };
    this.searchResultsHeaderWrapper = React.createRef();
  }

  public componentDidMount() {
    this.setState({
      resultsHeaderActive: true,
    });
  }

  public render() {
    const { l10n, searchResults, withMapButton, withFilterButton } = this.props;

    if (!searchResults) {
      return null;
    }

    const { isLoading, loyaltyMessagingModel, searchCriteria, sortAndFilter, resultsSummary } = searchResults;
    const pills = sortAndFilter && sortAndFilter.pills;
    const filters = searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.filters;
    const ResultsSubHeaderProps = {
      resultsSummary,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria,
    };
    const buttonsProps = {
      isActive: this.state.resultsHeaderActive,
      filters,
      dialogTitle: l10n.formatText('hotels.searchResults.filtersDialog.title'),
      withMapButton,
      withFilterButton,
    };

    const shouldRenderPills = pills && pills.length > 0;

    return (
      <ErrorBoundary label="hotel.search.tools">
        <UitkSpacing margin={{ blockstart: 'one' }}>
          <section data-stid="results-header" ref={this.searchResultsHeaderWrapper.value}>
            <ButtonsParent {...buttonsProps} />
            {shouldRenderPills && (
              <Experiment name="Small_Crafts_over_filtered_state_messaging">
                <ExperimentControl>
                  <Viewport>
                    <ViewSmall>
                      <UitkSpacing padding={{ blockend: 'three' }}>
                        <div>
                          <PlaybackFilter />
                        </div>
                      </UitkSpacing>
                    </ViewSmall>
                    <ViewLarge>
                      <PlaybackFilter wrapPills={true} />
                    </ViewLarge>
                  </Viewport>
                </ExperimentControl>
                <ExperimentVariant bucket={1} />
                <ExperimentVariant bucket={2} />
              </Experiment>
            )}
            <ResultsSubHeader {...ResultsSubHeaderProps} />
          </section>
        </UitkSpacing>
      </ErrorBoundary>
    );
  }
}

const Buttons = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton }: ButtonsProps) => (
  <UitkSpacing data-stid="tools__actions">
    <UitkLayoutFlex>
      {withFilterButton && (
        <UitkLayoutFlexItem grow={2} data-stid="filters-toggle-button">
          <UitkSpacing margin={'one'} padding={{ blockend: 'three' }}>
            <div>
              <FiltersToggle
                buttonComponent={UitkSecondaryButton}
                isActive={isActive}
                isFullWidth={true}
                label="hotels.searchResults.openFiltersDialog.label"
                a11yMessage="hotels.searchResults.appliedFiltersCount.a11yLabel"
                rfrr=":FilterControl"
                stid="open-hotel-filters"
                filters={filters}
                dialogParam="filter"
                dialogTitle={dialogTitle}
              />
            </div>
          </UitkSpacing>
        </UitkLayoutFlexItem>
      )}
      {withMapButton && (
        <UitkLayoutFlexItem grow={1} data-stid="map-preview-button">
          <UitkSpacing margin={'one'}>
            <div>
              <MapPreview isActive={isActive} />
            </div>
          </UitkSpacing>
        </UitkLayoutFlexItem>
      )}
    </UitkLayoutFlex>
  </UitkSpacing>
);

interface ButtonsProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  classes?: string;
}

const ButtonsParent = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton }: ButtonsParentProps) => (
  <Viewport>
    <ViewSmall>
      <Buttons isActive={isActive} filters={filters} dialogTitle={dialogTitle} withFilterButton={true} withMapButton={true} />
    </ViewSmall>
    <ViewLarge>
      <></>
    </ViewLarge>
    <ViewExtraLarge>
      <Buttons
        isActive={isActive}
        filters={filters}
        dialogTitle={dialogTitle}
        withMapButton={withMapButton}
        withFilterButton={withFilterButton}
      />
    </ViewExtraLarge>
  </Viewport>
);

interface ButtonsParentProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}
